import { formatQualityGuardCriticalityLabel, QG_NEXT_SEVERITY_CRITICALITY } from "@repo/shared";
import type { QgSectionState } from "@repo/shared/qg-next/types";
import {
  Contrast,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldQuestionMark,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export type QualityGuardScopeSeverity = "none" | "recommended" | "high" | "critical";

/**
 * LAT-858: coverage/evaluation-state taxonomy, orthogonal to issue severity. Describes whether
 * Quality Guard could form a verdict at all for the scope, not what the verdict was. Absent
 * (legacy rows) is treated as "complete" — no visual change from prior behavior.
 */
export type QualityGuardEvaluationState = "complete" | "partial" | "unchecked" | "not_applicable";

/** Rule-judgment breakdown backing the evaluation-state badge tooltip/aria copy. */
export interface QualityGuardEvaluationCounts {
  judged: number;
  notApplicable: number;
  unverified: number;
  errored: number;
}

/**
 * The display palette for an open issue: High red, Medium orange, Low Linear yellow. The three
 * hues are the `--qg-*` token family (`index.css`) rather than literals, so the ramp answers to
 * the theme like everything else and is named in one place.
 */
export const qualityGuardOpenSeverityVisuals = {
  critical: {
    textClassName: "text-qg-critical",
    tagClassName: "border-qg-critical/30 bg-qg-critical/10 text-qg-critical",
  },
  high: {
    textClassName: "text-qg-high",
    tagClassName: "border-qg-high/30 bg-qg-high/10 text-qg-high",
  },
  recommended: {
    textClassName: "text-qg-recommended",
    tagClassName: "border-qg-recommended/30 bg-qg-recommended/10 text-qg-recommended",
  },
} as const;

/** A risk somebody accepted on purpose — blue, because it is a decision and not a finding. */
export const qualityGuardAcceptedRiskVisuals = {
  textClassName: "text-qg-accepted",
  tagClassName: "border-qg-accepted/30 bg-qg-accepted/10 text-qg-accepted",
} as const;

/**
 * Work still owed — the informational `info` token, and an outline rather than a fill.
 *
 * Open work is not a severity and must not read as one: nothing is wrong, the section is
 * simply unfinished, and a section with a clean green shield beside a blue open-work tag
 * is a correct and expected state. So it takes neither the `--qg-*` severity ramp (whose
 * three steps are all "this is a defect, at this level") nor `warning` (which says a
 * decision is overdue), and it is drawn as a dashed outline — the draft idiom — rather
 * than as the filled chip a severity uses.
 */
export const qualityGuardOpenWorkVisuals = {
  textClassName: "text-info",
  tagClassName: "border-info/50 text-info",
} as const;

/** The one spelling of the axis, wherever it is named to a reader. */
export const QUALITY_GUARD_OPEN_WORK_LABEL = "Open work";

/** The panel group the open-work marker leads to, and the axis a finding carries. One string, so a marker and the group it opens cannot drift apart. */
export const QUALITY_GUARD_OPEN_WORK_GROUP_KEY = "openWork";

export const scopeSeverityClassNames: Record<QualityGuardScopeSeverity, string> = {
  none: "text-success",
  recommended: qualityGuardOpenSeverityVisuals.recommended.textClassName,
  high: qualityGuardOpenSeverityVisuals.high.textClassName,
  critical: qualityGuardOpenSeverityVisuals.critical.textClassName,
};

const mutedBadgeClassName = "text-muted-foreground";

const severityRank: Record<QualityGuardScopeSeverity, number> = {
  none: 0,
  recommended: 1,
  high: 2,
  critical: 3,
};

export function normalizeScopeSeverity(value: unknown): QualityGuardScopeSeverity {
  if (value === "critical" || value === "high" || value === "recommended" || value === "none") {
    return value;
  }
  // Legacy red grouped Critical and High. Treat it as High until a scan writes
  // exact semantic counters/highest-criticality fields; explicit Critical stays exact.
  if (value === "red") return "high";
  return value === "yellow" ? "recommended" : "none";
}

export function maxScopeSeverity(
  left: QualityGuardScopeSeverity,
  right: QualityGuardScopeSeverity
): QualityGuardScopeSeverity {
  return severityRank[right] > severityRank[left] ? right : left;
}

/**
 * The severity of one axis, from the exact counter with the legacy colour as the fallback.
 *
 * Older projections persisted only a coarse colour per axis; newer ones persist the exact
 * highest criticality. "None" from the exact field is what "this row predates the counter" and
 * "this axis is clean" both look like, so the legacy colour is consulted only there — and that
 * rule has to be the same wherever an axis severity is read, or the badge on an entity and the
 * counts in the specification list disagree about the same row.
 */
export function resolveAxisSeverity(
  exactSeverity: unknown,
  legacySeverity: unknown
): QualityGuardScopeSeverity {
  const exact = normalizeScopeSeverity(exactSeverity);
  return exact === "none" ? normalizeScopeSeverity(legacySeverity) : exact;
}

export function scopeSeverityClassName(args: {
  severity: QualityGuardScopeSeverity;
  applicable?: boolean;
  checking?: boolean;
}) {
  if (args.checking || args.applicable === false) {
    return mutedBadgeClassName;
  }
  return scopeSeverityClassNames[args.severity];
}

export function formatQualityGuardIssueCount(count: number | undefined | null): number {
  return typeof count === "number" && Number.isFinite(count) ? Math.max(0, Math.round(count)) : 0;
}

// LAT-858: copy + tone for the non-"complete" evaluation states. "unchecked" and "partial" share
// the warning family (same icon, different label) so they read as a coverage gap rather than a
// pass or a fail; "not_applicable" is deliberately muted/neutral — it isn't a gap at all.
const evaluationStateVisuals: Record<
  Exclude<QualityGuardEvaluationState, "complete">,
  { label: string; className: string }
> = {
  not_applicable: { label: "Not applicable", className: mutedBadgeClassName },
  unchecked: { label: "Couldn't check", className: "text-warning" },
  partial: { label: "Partially checked", className: "text-warning" },
};

/** Formats the judged/notApplicable/unverified/errored breakdown for tooltip/aria copy, e.g. "3 checked · 2 couldn't check". */
export function formatQualityGuardEvaluationCounts(
  counts: QualityGuardEvaluationCounts | null | undefined
): string | null {
  if (!counts) {
    return null;
  }
  const checked = formatQualityGuardIssueCount(counts.judged);
  const couldntCheck =
    formatQualityGuardIssueCount(counts.unverified) + formatQualityGuardIssueCount(counts.errored);
  const notApplicable = formatQualityGuardIssueCount(counts.notApplicable);
  const parts = [`${checked} checked`];
  if (couldntCheck > 0) {
    parts.push(`${couldntCheck} couldn't check`);
  }
  if (notApplicable > 0) {
    parts.push(`${notApplicable} not applicable`);
  }
  return parts.join(" · ");
}

export function combineQualityGuardIssueState(args: {
  completenessIssueCount?: number | null;
  completenessHighestCriticality?: unknown;
  completenessSeverity?: unknown;
  qualityIssueCount?: number | null;
  qualityHighestCriticality?: unknown;
  qualitySeverity?: unknown;
  criticalIssueCount?: number | null;
  highIssueCount?: number | null;
  recommendedIssueCount?: number | null;
}) {
  const count =
    formatQualityGuardIssueCount(args.completenessIssueCount) +
    formatQualityGuardIssueCount(args.qualityIssueCount);
  const criticalCount = formatQualityGuardIssueCount(args.criticalIssueCount);
  const highCount = formatQualityGuardIssueCount(args.highIssueCount);
  const recommendedCount = formatQualityGuardIssueCount(args.recommendedIssueCount);
  const countedSeverity: QualityGuardScopeSeverity =
    criticalCount > 0
      ? "critical"
      : highCount > 0
        ? "high"
        : recommendedCount > 0
          ? "recommended"
          : "none";
  const axisSeverity = maxScopeSeverity(
    resolveAxisSeverity(args.completenessHighestCriticality, args.completenessSeverity),
    resolveAxisSeverity(args.qualityHighestCriticality, args.qualitySeverity)
  );
  const severity = maxScopeSeverity(countedSeverity, axisSeverity);
  return {
    count,
    severity: count === 0 ? ("none" as const) : severity === "none" ? ("high" as const) : severity,
  };
}

export function QualityGuardIssueBadge({
  label,
  count,
  severity,
  applicable = true,
  checking = false,
  countNoun = "issue",
  countNounPlural = `${countNoun}s`,
  evaluationState,
  evaluationCounts,
  className,
}: {
  label: string;
  count: number | undefined | null;
  severity: QualityGuardScopeSeverity;
  applicable?: boolean;
  checking?: boolean;
  countNoun?: string;
  countNounPlural?: string;
  /** LAT-858: coverage/evaluation-state taxonomy. Absent = legacy "complete" (no visual change). */
  evaluationState?: QualityGuardEvaluationState;
  evaluationCounts?: QualityGuardEvaluationCounts | null;
  className?: string;
}) {
  const normalizedCount = formatQualityGuardIssueCount(count);
  const displayedSeverity = normalizedCount === 0 ? "none" : severity;
  const displayedSeverityLabel =
    displayedSeverity === "none" ? "None" : formatQualityGuardCriticalityLabel(displayedSeverity);
  // LAT-936: `checking` no longer hides the last known verdict. Every caller only ever passes
  // `checking: true` alongside a real prior projection — the true "never checked" case renders a
  // different placeholder before this component is reached at all (see
  // `QualityGuardEntityIssueChips`'s `ShieldQuestionMark` and `FieldHeader`'s
  // `showGuardPlaceholder`) — so `visibleValue`/the icon below are computed exactly as if not
  // checking, and a small refresh indicator (not a full spinner swap) carries the in-flight signal.
  const visibleValue = applicable ? (normalizedCount > 0 ? normalizedCount : null) : null;
  // A real finding always outranks coverage info: the evaluation-state display only kicks in
  // once there is nothing else to report — applicable, zero issues, and the evaluation state is a
  // non-"complete" value (absent/"complete" both mean legacy green). Shown during `checking` too:
  // it IS the last known state.
  const activeEvaluationState: QualityGuardEvaluationState | null =
    applicable && normalizedCount === 0 && evaluationState && evaluationState !== "complete"
      ? evaluationState
      : null;
  const evaluationCountsSummary = formatQualityGuardEvaluationCounts(evaluationCounts);
  const accessibleValue = applicable
    ? normalizedCount > 0
      ? `${displayedSeverityLabel} severity, ${normalizedCount} ${normalizedCount === 1 ? countNoun : countNounPlural}`
      : activeEvaluationState
        ? evaluationCountsSummary
          ? `${evaluationStateVisuals[activeEvaluationState].label}: ${evaluationCountsSummary}`
          : evaluationStateVisuals[activeEvaluationState].label
        : `no ${countNounPlural}`
    : "not applicable";
  const accessibleValueWithChecking = checking ? `${accessibleValue}, refreshing` : accessibleValue;
  const iconClassName = activeEvaluationState
    ? evaluationStateVisuals[activeEvaluationState].className
    : scopeSeverityClassName({
        severity: displayedSeverity,
        applicable,
      });

  return (
    <span
      role="img"
      data-quality-guard-issue-badge="true"
      data-qg-severity={displayedSeverity}
      data-qg-issue-count={normalizedCount}
      data-qg-count-noun={countNoun}
      data-qg-applicable={applicable ? "true" : "false"}
      data-qg-checking={checking ? "true" : "false"}
      data-qg-evaluation-state={activeEvaluationState ?? "complete"}
      className={cn(
        "inline-flex h-5 items-center justify-start gap-1 text-[11px] font-semibold leading-none",
        // LAT-936: stale-but-present while a re-check is in flight — reduced emphasis, never a
        // void. The last known icon/count stay on screen; the small corner spinner below (
        // `data-qg-checking-indicator`) carries the "this is refreshing" signal on its own.
        checking && "opacity-60",
        className
      )}
      title={`${label}: ${accessibleValueWithChecking}`}
      aria-label={`${label}: ${accessibleValueWithChecking}`}
    >
      <span className="relative inline-flex shrink-0">
        {!applicable ? (
          <Shield
            data-qg-severity-icon="true"
            data-qg-visual-state="not-applicable"
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        ) : activeEvaluationState === "not_applicable" ? (
          <ShieldOff
            data-qg-severity-icon="true"
            data-qg-visual-state="eval-not-applicable"
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        ) : activeEvaluationState === "unchecked" ? (
          <ShieldAlert
            data-qg-severity-icon="true"
            data-qg-visual-state="eval-unchecked"
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        ) : activeEvaluationState === "partial" ? (
          <ShieldAlert
            data-qg-severity-icon="true"
            data-qg-visual-state="eval-partial"
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        ) : normalizedCount === 0 ? (
          <ShieldCheck
            data-qg-severity-icon="true"
            data-qg-visual-state="none"
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        ) : (
          <ShieldAlert
            data-qg-severity-icon="true"
            data-qg-visual-state={displayedSeverity}
            className={cn("size-4", iconClassName)}
            strokeWidth={1.8}
            aria-hidden
          />
        )}
        {checking ? (
          <Spinner
            data-qg-checking-indicator="true"
            data-qg-visual-state="refreshing"
            className="-right-1 -top-1 absolute size-2.5 text-muted-foreground"
            aria-hidden
          />
        ) : null}
      </span>
      {visibleValue !== null ? (
        <span
          data-qg-issue-count-text="true"
          className="tabular-nums text-xs font-normal text-muted-foreground"
        >
          {visibleValue}
        </span>
      ) : null}
    </span>
  );
}

export function QualityGuardAcceptedRiskBadge({
  label,
  count,
  className,
  iconClassName,
  countClassName,
}: {
  label: string;
  count: number | undefined | null;
  className?: string;
  iconClassName?: string;
  countClassName?: string;
}) {
  const normalizedCount = formatQualityGuardIssueCount(count);
  if (normalizedCount === 0) {
    return null;
  }

  const accessibleValue = `${normalizedCount} accepted ${normalizedCount === 1 ? "risk" : "risks"}`;

  return (
    <span
      role="img"
      data-quality-guard-accepted-risk-badge="true"
      data-qg-accepted-risk-count={normalizedCount}
      className={cn(
        "inline-flex h-5 items-center justify-start gap-1 text-[11px] font-semibold leading-none",
        className
      )}
      title={`${label}: ${accessibleValue}`}
      aria-label={`${label}: ${accessibleValue}`}
    >
      <ShieldCheck
        data-qg-accepted-risk-icon="true"
        data-qg-visual-state="accepted"
        className={cn("size-4", qualityGuardAcceptedRiskVisuals.textClassName, iconClassName)}
        strokeWidth={1.8}
        aria-hidden
      />
      <span
        data-qg-accepted-risk-count-text="true"
        className={cn("tabular-nums text-xs font-normal text-muted-foreground", countClassName)}
      >
        {normalizedCount}
      </span>
    </span>
  );
}

/**
 * The open-work marker: how much is still owed on a scope, beside the issue badges and
 * never inside them.
 *
 * Rendered only when something is owed — a zero is not a state a reader needs told about,
 * and a tag that is always there would read as a fourth severity that happens to be
 * empty. `summaries` are the owed items in the judge's own words, shown on hover so a
 * reader can tell what is outstanding without opening the panel; `onClick` makes the tag
 * the way into the panel's Open work group, and without it the tag is inert text.
 */
export function QualityGuardOpenWorkTag({
  label = QUALITY_GUARD_OPEN_WORK_LABEL,
  count,
  summaries,
  onClick,
  className,
}: {
  label?: string;
  count: number | undefined | null;
  summaries?: readonly string[];
  onClick?: () => void;
  className?: string;
}) {
  const normalizedCount = formatQualityGuardIssueCount(count);
  if (normalizedCount === 0) {
    return null;
  }

  const accessibleValue = `${normalizedCount} ${normalizedCount === 1 ? "item" : "items"} owed`;
  const heading = `${label}: ${accessibleValue}`;
  const title = [heading, ...(summaries ?? []).map((summary) => `• ${summary}`)].join("\n");
  const shared = {
    "data-quality-guard-open-work-tag": "true",
    "data-qg-open-work-count": normalizedCount,
    title,
    "aria-label": heading,
    className: cn(
      // `rounded-xs` computes to 0 under this design system's `--radius`, so the tag is
      // square like every other chip; the dashed border is what makes it read as a
      // draft rather than as a verdict.
      "inline-flex h-5 items-center gap-1 rounded-xs border border-dashed px-1.5 text-[11px] font-semibold leading-none",
      qualityGuardOpenWorkVisuals.tagClassName,
      className
    ),
  } as const;
  const content = (
    <>
      <Contrast
        data-qg-open-work-icon="true"
        className="size-3.5 shrink-0"
        strokeWidth={1.8}
        aria-hidden
      />
      <span data-qg-open-work-count-text="true" className="tabular-nums">
        {normalizedCount}
      </span>
    </>
  );

  if (!onClick) {
    return (
      <span role="img" {...shared}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      {...shared}
      className={cn(
        shared.className,
        "transition-colors hover:bg-surface-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      )}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {content}
    </button>
  );
}

/**
 * The state an element's own badge shows. An empty section shows nothing unless a rule requires
 * content, and then its finding gives it that finding's state; so an element whose worst state is
 * `empty` shows the state of its judged sections (`green` when one passed), and an element with
 * nothing but empty sections shows no state at all (null).
 */
export function elementBadgeState(
  state: QgSectionState | null,
  scopeStates: readonly QgSectionState[]
): QgSectionState | null {
  if (state !== "empty") return state;
  return scopeStates.some((scopeState) => scopeState === "green") ? "green" : null;
}

/** The word a Quality Guard next state is read as, beside its shield. */
export function formatQgNextStateLabel(state: QgSectionState): string {
  switch (state) {
    case "green":
      return "No issues";
    case "pending":
      return "Checking";
    case "not_checked":
      return "Not checked";
    case "empty":
      return "Empty";
    default:
      return formatQualityGuardCriticalityLabel(QG_NEXT_SEVERITY_CRITICALITY[state]);
  }
}

/**
 * The shield of one Quality Guard next scope: green when every applicable rule passed, the
 * colour of the highest open severity, or grey while it is checked or when a rule could not be
 * checked; an empty section shows the word "Empty" and no shield. A section carries no number;
 * `count` is given only for an element, where it is the number of its scopes with an issue.
 */
export function QgNextStateBadge({
  label,
  state,
  count,
  className,
}: {
  label: string;
  state: QgSectionState;
  count?: number;
  className?: string;
}) {
  const word = formatQgNextStateLabel(state);
  const accessible =
    count !== undefined && count > 0
      ? `${word}, ${count} ${count === 1 ? "section" : "sections"} with issues`
      : word;
  const iconProps = {
    "data-qg-severity-icon": "true",
    strokeWidth: 1.8,
    "aria-hidden": true,
  } as const;
  return (
    <span
      role="img"
      data-qg-next-state={state}
      className={cn(
        "inline-flex h-5 items-center justify-start gap-1 text-[11px] font-semibold leading-none",
        className
      )}
      title={`${label}: ${accessible}`}
      aria-label={`${label}: ${accessible}`}
    >
      <span className="relative inline-flex shrink-0">
        {state === "green" ? (
          <ShieldCheck {...iconProps} className={cn("size-4", scopeSeverityClassNames.none)} />
        ) : state === "pending" ? (
          <ShieldQuestionMark {...iconProps} className={cn("size-4", mutedBadgeClassName)} />
        ) : state === "not_checked" ? (
          <ShieldOff {...iconProps} className={cn("size-4", mutedBadgeClassName)} />
        ) : state === "empty" ? (
          // An empty section was not checked: it carries the word, never a shield, so it can
          // never read as green.
          <span data-qg-empty-word="true" className="text-[11px] font-normal text-muted-foreground">
            {word}
          </span>
        ) : (
          <ShieldAlert
            {...iconProps}
            className={cn("size-4", scopeSeverityClassNames[QG_NEXT_SEVERITY_CRITICALITY[state]])}
          />
        )}
        {state === "pending" ? (
          <Spinner
            data-qg-checking-indicator="true"
            className="-right-1 -top-1 absolute size-2.5 text-muted-foreground"
            aria-hidden
          />
        ) : null}
      </span>
      {count !== undefined && count > 0 ? (
        <span
          data-qg-issue-count-text="true"
          className="tabular-nums text-xs font-normal text-muted-foreground"
        >
          {count}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The current dismissals counted on an element or a folder of the Quality Guard next project
 * view, in the accepted visuals the Dismissed column takes over. Nothing is drawn for none, as
 * with accepted risks.
 */
export function QgNextDismissedBadge({
  label,
  count,
  className,
}: {
  label: string;
  count: number;
  className?: string;
}) {
  if (count <= 0) return null;
  const accessible = `${count} ${count === 1 ? "dismissal" : "dismissals"}`;
  return (
    <span
      role="img"
      data-qg-next-dismissed-badge="true"
      className={cn(
        "inline-flex h-5 items-center justify-start gap-1 text-[11px] font-semibold leading-none",
        className
      )}
      title={`${label}: ${accessible}`}
      aria-label={`${label}: ${accessible}`}
    >
      <ShieldCheck
        className={cn("size-4", qualityGuardAcceptedRiskVisuals.textClassName)}
        strokeWidth={1.8}
        aria-hidden
      />
      <span className="tabular-nums text-xs font-normal text-muted-foreground">{count}</span>
    </span>
  );
}
